import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import mousify from './mousify'
import Snapper from './snap'
import leftClick from '../left-click'
import Box from './box'
import { Point } from './vector'
import { rotate } from './vector'

/*
 * Defines all possible handles. Each handle has a type and
 * a position. The position is the (x,y) position of the handle within the
 * wob. The position is used when resizing a wob.
 */
const HANDLE_LOCATIONS = {
  tl: { type: 'tl', position: [0.0, 0.0], constraints: [-1, -1] },
  tc: { type: 'tc', position: [0.5, 0.0], constraints: [0, -1] },
  tr: { type: 'tr', position: [1.0, 0.0], constraints: [1, -1] },
  rm: { type: 'rm', position: [1.0, 0.5], constraints: [1, 0] },
  br: { type: 'br', position: [1.0, 1.0], constraints: [1, 1] },
  bc: { type: 'bc', position: [0.5, 1.0], constraints: [0, 1] },
  bl: { type: 'bl', position: [0.0, 1.0], constraints: [-1, 1] },
  lm: { type: 'lm', position: [0.0, 0.5], constraints: [-1, 0] }
}

var Handle = Backbone.View.extend({
  className: 'resize-handle',

  events: {
    'mousedown': 'listen',
    'touchstart': 'listen'
  },

  initialize: function (options) {
    this.type = options.type
    this.canvas = options.canvas
    this.position = options.position
    this.constraints = options.constraints
    this.wob = options.wob
    this.resizer = options.resizer

    this.listenTo(this.wob, 'change:rotate', this.flip)

    this.flip()
    this.$el.addClass(this.type)
    // Where we scale from -- opposite side or corner from the moving handle
    this.originPoint = [1 - this.position[0], 1 - this.position[1]]

    _.bindAll(this, 'drop', 'drag')
  },

  flip: function () {
    this.flipped = this.wob.isFlipped()

    this.$el.toggleClass('handle-flipped', this.flipped)
  },

  /**
   * Returns the {x,y} coordinate of the area at the incoming position.
   * Defaults to the current handle position
   */
  getAbsolutePoint: function (area, position) {
    if (!position) {
      position = this.position
    }
    let center = this.wob.center()
      , point = new Point(area.left + area.width * position[0], area.top + area.height * position[1])
      , rotated = rotate(this.wob.get('rotate'), new Point(center.x, center.y))(point)

    return {
      left: rotated.x,
      top: rotated.y
    }
  },

  listen: _.wrap(function (e) {
    e.preventDefault()
    e.stopPropagation()

    let event = mousify(e)
      , coords = this.canvas.coords(event)
      , handlePoint = this.getAbsolutePoint(this.wob.area())
      , area = this.wob.area()

    // Store the original offset of this click from the actual wob coordinates
    this.clickOffset = {
      left: coords.left - handlePoint.left,
      top: coords.top - handlePoint.top
    }

    $(document).on('mousemove touchmove', this.drag)
    $(document).on('mouseup touchend', this.drop)

    this.resizer.start()
  }, leftClick),

  drag: function (e) {
    let event = mousify(e)
      , coords = this.canvas.coords(event)
      , handlePoint = this.getAbsolutePoint(this.wob.area())

    let difference = {
      left: coords.left - handlePoint.left - this.clickOffset.left,
      top: coords.top - handlePoint.top - this.clickOffset.top
    }

    difference = this.angleAdjustment(difference)

    this.resizer.resize(this, difference, e.shiftKey, coords)
  },

  /*
   * Adjusts the diff based on the rotation of this wob
   */
  angleAdjustment: function (diff) {
    const angle = this.wob.get('rotate') || 0

    if (angle >= 45 && angle < 135) {
      // flipped right
      let [top, left] = [-diff.left, diff.top]
      diff.left = left
      diff.top = top
    } else if (angle >= 135 && angle < 225) {
      // upside down
      diff.left *= -1
      diff.top *= -1
    } else if (angle >= 225 && angle < 315) {
      let [top, left] = [diff.left, -diff.top]
      diff.left = left
      diff.top = top
    }

    return diff
  },

  drop: function (e) {
    this.resizer.end()
    $(document).unbind('mousemove touchmove', this.drag)
    $(document).unbind('mouseup touchend', this.drop)
  }

})

var Resizer = Backbone.View.extend({
  className: 'resizing',

  initialize: function (options) {
    this.wob = options.wob
    this.canvas = options.canvas

    this.listenTo(this.wob, 'change:locked', function (wob) {
      this.toggle(this.wob.selected && !wob.get('locked'))
    })

    this.listenTo(this.wob, 'change:selected', function (wob, bool) {
      this.toggle(bool && !this.wob.get('locked'))
    })

    this.toggle(this.wob.selected)
  },

  render: function () {
    var locations = []

    const resizable = _.result(this.wob, 'resizable')
    const lockedAR = _.result(this.wob, 'lockedAR')

    if (typeof resizable === 'object' && (resizable.width || resizable.height)) {
      if (resizable.width && !resizable.height && !lockedAR) {
        // Able to resize the width, but not the height
        locations = [HANDLE_LOCATIONS.lm, HANDLE_LOCATIONS.rm]
      } else if (resizable.height && !resizable.width && !lockedAR) {
        // Able to resize the height, but not the width
        locations = [HANDLE_LOCATIONS.tc, HANDLE_LOCATIONS.bc]
      } else {
        // Both width and height are resizable
        locations = _.values(HANDLE_LOCATIONS)
      }
    } else if (resizable) {
      locations = _.values(HANDLE_LOCATIONS) // All of them
    }

    this.handlesEl = $('<div>')
    this.handles = {} // Object of handles key'd on location. Mostly used for testing
    locations.forEach(location => {
      let handle = this.handles[location.type] = new Handle({type: location.type, position: location.position, constraints: location.constraints, canvas: this.canvas, wob: this.wob, resizer: this })
      this.handlesEl.append(handle.render().el)
    })
    this.$el.append(this.handlesEl)
    return this
  },

  remove: function () {
    if (this.handlesEl) {
      this.handlesEl.remove()
    }
    if (this.handles) {
      Object.keys(this.handles).forEach(handle => this.handles[handle].remove())
      this.handles = null
    }
    this.resizerIcon = null
  },

  toggle: function (bool) {
    bool ? this.render() : this.remove()
  },

  start: function () {
    this.trigger('start')

    // Set a class on the canvas indicating we're resizing
    this.canvas.$el.addClass('is-resizing')

    let view = this.canvas.views[this.wob.cid]
    if (view) {
      view.$el.trigger('start:resize') // Fire `start:resize` on the actual wob `el`
    }
    this.resizing = true
    this.snap = new Snapper({canvas: this.canvas, el: this.canvas.el})

    let area = this.wob.area()

    // Store min widths/heights as aspect ratios
    this.minWidth = _.result(this.wob.widget.containerDefaults, 'minWidth', 20)
    this.minHeight = _.result(this.wob.widget.containerDefaults, 'minHeight', 20)
    this.aspectRatio = this.wob.getAspectRatio()

    // Initialize a new box object based on wob dimensions, used for nifty calculations,
    // without needed to worry about messing with the backbone wob model.
    this.box = new Box(area.left, area.top, area.left + area.width, area.top + area.height)
    this.box.setMinWidth(this.minWidth)
    this.box.setMinHeight(this.minHeight)

    this.canvas.disableWobEvents()
    this.canvas.history.record()
  },

  end: function () {
    this.canvas.$el.removeClass('is-resizing')
    this.trigger('end')

    this.resizing = false
    let view = this.canvas.views[this.wob.cid]
    if (view) {
      view.$el.trigger('end:resize') // Fire `end:resize` on the actual wob `el` in case there are interested parties knowing a wob was resized
    }
    this.canvas.enableWobEvents()
    this.snap.release()
    this.snap = null
  },

  resize: function (handle, diff, lockAR, mouseCoords) {
    this.trigger('resize')
    let area = this.wob.area() // current area of the wob

    // Adjust our box based on the diff
    let width = this.box.width()
      , height = this.box.height()
      , ratioMode = this.aspectRatio >= 1 ? 'width' : 'height' // default based on the aspect ratio
      , lockedAR = lockAR || _.result(this.wob, 'lockedAR')
      , angle = this.wob.get('rotate') || 0

    if (lockedAR) {
      // Determine if we want to grow the width or height
      if (!handle.constraints[0] && !!handle.constraints[1]) {
        // Moving a top/bottom handle
        ratioMode = 'width'
      } else if (!!handle.constraints[0] && !handle.constraints[1]) {
        // Moving a right/left handle
        ratioMode = 'height'
      }

      if (ratioMode === 'width') {
        // Automatically growing the width, so ignore the diff.left
        diff.left = 0
      } else {
        // Automatically growing the height, so ignore the diff.top
        diff.top = 0
      }
    }

    width += (diff.left * handle.constraints[0])
    height += (diff.top * handle.constraints[1])

    this.box.resize(width, height, handle.originPoint, angle, lockedAR ? ratioMode : null, lockedAR ? this.aspectRatio : null)

    let virtualArea = {
      left: this.box.x1,
      top: this.box.y1,
      width: this.box.width(),
      height: this.box.height()
    }

    let snapDiff = this.snap.snap(virtualArea, area, handle.type) // Now check if we need to snap

    if (lockedAR && ratioMode === 'width' && snapDiff.left) {
      // Snap wants us to adjust the width, but that grows automatically since we have lockedAR
      let lockedWidth = this.box.width() + snapDiff.left * handle.constraints[0]
      height = this.aspectRatio * lockedWidth
    } else if (lockedAR && ratioMode === 'height' && snapDiff.top) {
      // Snap wants us to adjust the height, but that grows automatically since we have lockedAR
      let lockedHeight = this.box.height() + snapDiff.top * handle.constraints[1]
      width = lockedHeight / this.aspectRatio
    } else {
      // Apply the snap diffs
      width += ((snapDiff.left || 0) * handle.constraints[0])
      height += ((snapDiff.top || 0) * handle.constraints[1])
    }

    // Now resize the box again based on the snap recommendations
    this.box.resize(width, height, handle.originPoint, angle, lockedAR ? ratioMode : null, lockedAR ? this.aspectRatio : null)

    // Update the wob object
    this.wob.set({
      left: this.box.x1,
      top: this.box.y1,
      width: this.box.width(),
      height: this.box.height()
    }, { handle })

    if (Object.keys(this.wob.changed).length > 0) {
      // Only fire resize if the size changed
      this.wob.trigger('resize', this.wob, area, {handle})
    }
  },

  release: function () {
    this.undelegateEvents()
    if (this.snap) {
      this.snap.release()
      this.snap = null
    }
  }

})

export default Resizer
